<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "@/common/utils/toast";

import { maskPhone } from "@/common/utils/mask";

import { baseInput, baseSelect, baseTextarea } from "@/common/components";

import type { ContactType } from "@/common/types/common";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: ContactType | null;
    title?: string;
    width?: string;
  }>(),
  {
    width: "100%",
  }
);

const defaultData = {
  name: "",
  type: "",
  contact: "",
  description: "",
};

const payload = ref<ContactType>({ ...defaultData });

const typeOptions = computed(() => [
  { value: "phone", option: t("common.labels.phone") },
  { value: "email", option: t("common.labels.email") },
  { value: "other", option: t("common.labels.other") },
]);

function update() {
  emit("update-data", payload.value);
}

function typeMask(value: string) {
  if (payload.value.type === "phone") return maskPhone(value);

  return value;
}

function placeholder() {
  if (payload.value.type === "phone") {
    return "(00) 00000-0000";
  }

  if (payload.value.type === "email") {
    return "example@example.com";
  }

  return "";
}

watchEffect(() => {
  if (props.data) {
    payload.value = { ...props.data };
  }
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <div v-if="title" class="title">
      {{ title }}
    </div>
    <form>
      <base-input
        v-model="payload.name"
        :value="payload.name"
        @input="update()"
      >
        <template #label> {{ t("common.labels.name") }} * </template>
      </base-input>

      <div class="flex flex-row gap-2">
        <div class="flex-1">
          <base-select
            v-model="payload.type"
            :value="payload.type"
            :options="typeOptions"
            @change="update()"
          >
            <template #label> {{ t("common.labels.type") }} * </template>
          </base-select>
        </div>

        <div class="flex-2">
          <base-input
            v-model="payload.contact"
            :value="payload.contact"
            :mask="typeMask"
            :placeholder="placeholder()"
            @input="update()"
          >
            <template #label> {{ t("common.labels.contact") }} * </template>
          </base-input>
        </div>
      </div>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        @input="update()"
      >
        <template #label> {{ t("common.labels.description") }} </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
